/**
 * WebRTCAdapter
 * Provides unified interface for Chrome and Firefox
 */
namespace SmartVideo {
  export class WebRTCAdapter {
    browser: string;
    isChrome: boolean;
    isFireFox: boolean;

    constructor(browser: string) {
      switch (browser) {
        case 'Opera':
          this.isChrome = true;
          break;
        case 'Chrome':
          this.isChrome = true;
          break;
        case 'SmartVideoAgent':
          this.isChrome = true;
          break;
        case 'Firefox':
          this.isFireFox = true;
          break;
        default:
          break;
      }

      this.browser = browser;
      // this.setUserMedia();
    }

    detachMediaStream(
      element: HTMLVideoElement,
      stream: MediaStream,
      isLocal: boolean,
      stop: boolean
    ) {
      console.log('Detaching media stream ', element, ' stream ', stream);
      if (element) {
        element.pause();
        element.src = '';
      }

      // stopping stream (camera, ...)

      if (stream) {
        if (this.isFireFox) {
          // no stop method on remote streams in firefox
          if (isLocal && stop) {
            this.stopMediaStream(stream);
          }
        } else {
          if (stop) {
            this.stopMediaStream(stream);
          }
        }
      }
    }

    stopMediaStream(stream: MediaStream) {
      const tracks = stream.getTracks();
      for (let i = 0; i < tracks.length; i++) {
        tracks[i].stop();
      }
    }

    emptyAudioVideoSources(callback: Function) {
      const audioSources = [{ id: '0', label: 'Default' }];
      const videoSources = [{ id: '0', label: 'Default' }];
      setTimeout(function () {
        callback(audioSources, videoSources);
      }, 100);
    }

    fixConfig(pcConfig: RTCConfiguration) {
      if (pcConfig === null) {
        return;
      }

      for (let i = 0; i < pcConfig.iceServers.length; i++) {
        if (pcConfig.iceServers[i].hasOwnProperty('urls')) {
          pcConfig.iceServers[i]['url'] = pcConfig.iceServers[i]['urls'];
          delete pcConfig.iceServers[i]['urls'];
        }
      }
    }

    createIceServer(url: string, username: string, password: string) {
      let iceServer = null;
      const urlParts = url.split(':');

      if (urlParts[0].indexOf('stun') === 0) {
        // Create iceServer with stun url.
        iceServer = {
          url: url,
        };
      } else if (urlParts[0].indexOf('turn') === 0) {
        // Create iceServer with turn url.
        iceServer = {
          url: url,
          credential: password,
          username: username,
        };
      }
      return iceServer;
    }

    createIceServers(urls, username: string, password: string) {
      let iceServers;
      iceServers = {
        urls: urls,
        credential: password,
        username: username,
      };
      return iceServers;
    }

    attachMediaStream(element: HTMLVideoElement, stream: MediaStream) {
      console.log('Attaching Media Stream ', element, '  ', stream);
      if (!element) {
        console.log('Error attaching stream to element, element is invalid');
        return;
      }
      if (typeof element.srcObject !== 'undefined') {
        element.srcObject = stream;
      } else if (typeof element.src !== 'undefined') {
        element.src = URL.createObjectURL(stream);
      } else {
        console.log('Error attaching stream to element.');
      }
    }

    reattachMediaStream(
      toVideoElement: HTMLVideoElement,
      fromVideoElement: HTMLVideoElement
    ) {
      console.log('Reattaching media stream');
      if (this.isFireFox) {
        toVideoElement.srcObject = fromVideoElement.srcObject;
        toVideoElement.play();
      } else if (this.isChrome) {
        toVideoElement.src = fromVideoElement.src;
      }
    }

    getRTCSessionDescription() {
      // Firefox and Chrome both now support without prefix
      return RTCSessionDescription;
    }

    getRTCIceCandidate() {
      // Firefox and Chrome both now support without prefix
      return RTCIceCandidate;
    }

    getWebRTCSources(callback: Function, secondTime?: boolean) {
      if (this.isFireFox) {
        this.emptyAudioVideoSources(callback);
      } else if (this.isChrome) {
        const self = this;
        if (location.protocol !== 'https:') {
          this.emptyAudioVideoSources(callback);
          return;
        }

        // On chrome mobile, https does not store the permissions, hence it goes into an infinite loop of asking for permission
        if (secondTime === undefined) {
          secondTime = false;
        }

        navigator.mediaDevices.enumerateDevices().then(function (sourceInfos) {
          const audioSources = [];
          const videoSources = [];
          let hasVideo = false;
          let hasAudio = false;
          for (let i = 0; i < sourceInfos.length; ++i) {
            const sourceInfo = sourceInfos[i];
            if (!sourceInfo.label) {
              if (secondTime) {
                self.emptyAudioVideoSources(callback);
                return;
              }
              switch (sourceInfo.kind) {
                case 'videoinput':
                  hasVideo = true;
                  break;
                case 'audioinput':
                  hasAudio = true;
                  break;
                default:
                  break;
              }
              const mediaOptions = {
                video: hasVideo,
                audio: hasAudio,
              };
              /* Chrome has this 'security' feature where labels are given only
                if the user has granted permission to access microphone/camera */
              navigator.mediaDevices
                .getUserMedia(mediaOptions)
                .then(function (stream) {
                  self.stopMediaStream(stream);
                  self.getWebRTCSources(callback, true);
                  return;
                })
                .catch(function (err) {
                  console.error('Denied getUserMedia - using Default sources');
                  self.emptyAudioVideoSources(callback);
                  return;
                });
            }
            console.dir(sourceInfo);
            if (sourceInfo.kind === 'audioinput') {
              const audioSource = {
                id: sourceInfo.deviceId,
                label: sourceInfo.label.replace(
                  /\([a-zA-Z0-9]+:[a-zA-Z0-9]+\)/,
                  ''
                ),
              };
              audioSources.push(audioSource);
            } else if (sourceInfo.kind === 'videoinput') {
              const videoSource = {
                id: sourceInfo.deviceId,
                label: sourceInfo.label.replace(
                  /\([a-zA-Z0-9]+:[a-zA-Z0-9]+\)/,
                  ''
                ),
              };
              if (videoSource.label !== 'VidyoCamera') {
                videoSources.push(videoSource);
              } else {
                console.log('Dropping VidyoCamera');
              }
            } else {
              console.log('Some other kind of source: ', sourceInfo);
            }
          }
          callback(audioSources, videoSources);
        });
      }
    }

    getWebRTCStats(
      pc: RTCPeerConnection,
      track: MediaStreamTrack,
      callback: Function
    ) {
      /* Start using only WebRTC compliant stats, instead of separate logic for FireFox/Google
        The below stats being retrieved should be safe in both browsers, because they are simple
        statistics */
      pc.getStats(track)
        .then(function (rtcStatsReport) {
          const stats = <any>{};
          stats.videoResolution = {};
          stats.videoResolution.height = 0;
          stats.videoResolution.width = 0;

          for (const stat in rtcStatsReport) {
            if (stat.indexOf('rtp') !== -1) {
              if (stat.indexOf('inbound') !== -1) {
                stats.firs = 0;
                stats.nacks = 0;
                stats.videoFrameRate = Math.floor(
                  rtcStatsReport[stat]['framesDecoded']
                );
                stats.videoDecodedFrameRate = stats.videoFrameRate;
                stats.videoDisplayedFrameRate = stats.videoFrameRate;
                stats.bytesReceived = rtcStatsReport[stat]['bytesReceived'];
                stats.packetsReceived = rtcStatsReport[stat]['packetsReceived'];
                stats.packetsLost = rtcStatsReport[stat]['packetsLost'];
                callback(stats);
                return;
              } else if (stat.indexOf('outbound') !== -1) {
                stats.captureFrameRate = Math.floor(
                  rtcStatsReport[stat]['framesEncoded']
                );
                stats.encodeFrameRate = stats.captureFrameRate;
                stats.numFirs = 0;
                stats.numNacks = 0;
                stats.mediaRTT = 0;
                stats.bytesSent = rtcStatsReport[stat]['bytesSent'];
                callback(stats);
                return;
              }
            }
          }
        })
        .catch(function (err) {
          callback({ error: err });
        });
    }
  }
}
